/**
 * Membership pricing display for pre-purchase surfaces (product cards + PDP).
 *
 * Cart/checkout discounting is handled natively by Shopify; this module only
 * renders the member-facing price preview the API can't surface server-side.
 *
 * Data source:
 *   member (logged-in, matching tag): bundle-subscriptions (Awtomic) API —
 *     store proxy `tokenPath` -> { customerId, authToken }
 *     -> GET `${apiBase}/customers/:id/membership-discounts` (JWT).
 *   non-member / logged-out: the per-product `hallwines.base_member_discount`
 *     metafield (a percentage), rendered as a teaser price. No network needed.
 *
 * Rule evaluation follows the API contract: rules are pre-sorted in priority
 * order, first match wins, usage rules require remainingUsage > 0.
 *
 * @module membership-pricing
 */

import { formatMoney } from '@theme/money-formatting';

const CONFIG = window.__membershipPricing;
const CACHE_PREFIX = 'hallwines:membership-pricing:';

/** @typedef {{ strategy: string, type: string, discountAmount: number, remainingUsage: (number|null), displayLabel?: string, productGIds?: string[], productVariantGIds?: string[], collectionGIds?: string[], resolvedProductGIds?: string[], productTypes?: string[] }} Rule */

/** In-memory promise so concurrent callers share one network round-trip. */
let profilePromise = null;

/**
 * @param {number} cents
 * @returns {string}
 */
function money(cents) {
  return formatMoney(cents, CONFIG.moneyFormat, CONFIG.currency);
}

/**
 * Reads a cached discount profile if present and unexpired.
 * @param {string} key
 * @returns {Rule[]|null}
 */
function readCache(key) {
  if (!CONFIG.cacheMs) return null;
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.exp !== 'number' || parsed.exp < Date.now()) return null;
    return parsed.rules;
  } catch (e) {
    return null;
  }
}

/**
 * @param {string} key
 * @param {Rule[]} rules
 */
function writeCache(key, rules) {
  if (!CONFIG.cacheMs) return;
  try {
    sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ rules, exp: Date.now() + CONFIG.cacheMs }));
  } catch (e) {
    // sessionStorage full or unavailable — degrade to per-request fetches.
  }
}

/**
 * Fetches the logged-in customer's discount rules via the store proxy + API.
 * @returns {Promise<Rule[]>}
 */
async function fetchMemberRules() {
  if (!CONFIG.apiBase || !CONFIG.tokenPath) return [];

  const tokenRes = await fetch(CONFIG.tokenPath, { credentials: 'same-origin' });
  if (!tokenRes.ok) return [];
  const token = await tokenRes.json();
  if (!token || !token.customerId || !token.authToken) return [];

  const url = `${CONFIG.apiBase}/customers/${encodeURIComponent(token.customerId)}/membership-discounts`;
  const res = await fetch(url, { headers: { Authorization: token.authToken } });
  if (!res.ok) return [];
  const body = await res.json();
  return Array.isArray(body && body.membershipDiscounts) ? body.membershipDiscounts : [];
}

/**
 * Resolves the member discount rules for the current visitor, cached per
 * session. Non-members have no rules — their teaser comes from the per-product
 * base-discount metafield (see apply), so we skip the network for them.
 * @returns {Promise<Rule[]>}
 */
function loadProfile() {
  if (profilePromise) return profilePromise;

  if (!CONFIG.loggedIn) {
    profilePromise = Promise.resolve([]);
    return profilePromise;
  }

  // Scope the cache to the customer so a different login in the same tab can't
  // read the previous customer's discounts.
  const key = 'member:' + (CONFIG.customerId || 'anon');
  const cached = readCache(key);
  if (cached) {
    profilePromise = Promise.resolve(cached);
    return profilePromise;
  }

  profilePromise = fetchMemberRules()
    .then((rules) => {
      writeCache(key, rules);
      return rules;
    })
    .catch(() => []);

  return profilePromise;
}

/**
 * Drops the cached discount profile(s) and the in-flight promise so the next
 * loadProfile() refetches. Called when the visitor joins or signs in, since
 * either can change their membership.
 */
function clearProfileCache() {
  profilePromise = null;
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith(CACHE_PREFIX))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch (e) {
    // sessionStorage unavailable — nothing to clear.
  }
}

/**
 * Returns true when a rule's criteria match the given item.
 * @param {Rule} rule
 * @param {{ productGid: string, variantGid: string, productType: string, collectionGids: string[] }} item
 * @returns {boolean}
 */
function ruleMatches(rule, item) {
  switch (rule.strategy) {
    case 'ALL':
      return true;
    case 'PRODUCTS':
      return Array.isArray(rule.productGIds) && rule.productGIds.includes(item.productGid);
    case 'PRODUCT_VARIANTS':
      return Array.isArray(rule.productVariantGIds) && rule.productVariantGIds.includes(item.variantGid);
    case 'COLLECTIONS':
      // Prefer the API's pre-expanded product list: product.collections (item.collectionGids)
      // only exposes collections published to the Online Store channel, so membership
      // collections that aren't published there would never match by collection GID.
      return (
        (Array.isArray(rule.resolvedProductGIds) && rule.resolvedProductGIds.includes(item.productGid)) ||
        (Array.isArray(rule.collectionGIds) && rule.collectionGIds.some((gid) => item.collectionGids.includes(gid)))
      );
    case 'TYPES':
      return Array.isArray(rule.productTypes) && rule.productTypes.includes(item.productType);
    default:
      return false;
  }
}

/**
 * Finds the first applicable discount rule for an item, or null.
 * @param {Rule[]} rules
 * @param {{ productGid: string, variantGid: string, productType: string, collectionGids: string[] }} item
 * @returns {Rule|null}
 */
function resolveDiscount(rules, item) {
  for (const rule of rules) {
    if (rule.type === 'usage' && !(typeof rule.remainingUsage === 'number' && rule.remainingUsage > 0)) {
      continue;
    }
    if (ruleMatches(rule, item)) {
      return rule;
    }
  }
  return null;
}

/**
 * Reads the evaluation item from a <product-price> element's data attributes.
 * @param {HTMLElement} el
 * @returns {{ productGid: string, variantGid: string, productType: string, collectionGids: string[], price: number, baseDiscount: number|null }|null}
 */
function readItem(el) {
  const price = parseInt(el.dataset.mpPrice ?? '', 10);
  if (!el.dataset.mpProductGid || isNaN(price)) return null;
  const collections = (el.dataset.mpCollectionGids || '').split(',').filter(Boolean);
  const baseDiscount = parseFloat(el.dataset.mpBaseDiscount ?? '');
  return {
    productGid: el.dataset.mpProductGid,
    variantGid: el.dataset.mpVariantGid || '',
    productType: el.dataset.mpProductType || '',
    collectionGids: collections,
    price,
    baseDiscount: isNaN(baseDiscount) ? null : baseDiscount,
  };
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value).replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c)
  );
}

/**
 * Builds an underlined link, or a bare span when no URL is configured.
 * @param {string} text
 * @param {string} url
 * @returns {string}
 */
function link(text, url) {
  const safe = escapeHtml(text);
  return url ? `<a class="member-price__link" href="${escapeHtml(url)}">${safe}</a>` : `<span>${safe}</span>`;
}

/**
 * Renders the member-price markup for a resolved discount.
 * @param {HTMLElement} shell - the <member-price> element
 * @param {number} regularCents
 * @param {number} discountPercent
 * @param {'api'|'metafield'} source - where the discount came from (debug aid)
 * @param {boolean} isMember - render the member price layout vs the teaser
 * @param {Rule|null} [rule] - the matched API rule (drives the thank-you copy)
 */
function render(shell, regularCents, discountPercent, source, isMember, rule) {
  const memberCents = Math.round((regularCents * (100 - discountPercent)) / 100);
  const context = shell.dataset.mpContext || 'card';
  const s = CONFIG.strings;
  const reg = money(regularCents);
  const member = money(memberCents);

  let html = '';

  if (isMember) {
    // "Your Price: $X | $Y" with the retail price struck through. Rewards members
    // drop the "Your Price:" prefix on the PDP only — cards keep it.
    const isRewards = (rule && rule.displayLabel || '').toLowerCase().includes('rewards');
    const primary = isRewards && context === 'pdp' ? member : `${s.yourPrice} ${member}`;
    html =
      `<span class="member-price__primary">${escapeHtml(primary)}</span>` +
      `<span class="member-price__divider" aria-hidden="true"></span>` +
      `<s class="member-price__was">${escapeHtml(reg)}</s>`;
    if (context === 'pdp') {
      // Rewards members get a "| Join the Club" CTA after the struck price.
      if (isRewards) {
        html += `<span class="member-price__divider" aria-hidden="true"></span>` + link(s.join, CONFIG.links.join);
      }
      // "Rewards" programs get their own thank-you copy; otherwise the base one.
      const thanks = isRewards ? s.thanksRewards || s.thanks : s.thanks;
      if (thanks) html += `<span class="member-price__note">${escapeHtml(thanks)}</span>`;
    }
  } else if (context === 'pdp') {
    // Logged out: "Join the Club or Sign In". Logged-in non-member: just "Join
    // the Club" — they're already signed in, so the Sign In link is dropped.
    let cta = link(s.join, CONFIG.links.join);
    if (!CONFIG.loggedIn) {
      const signin = `<a class="member-price__link" href="#" data-member-price-signin>${escapeHtml(s.signin)}</a>`;
      cta = `${cta} ${escapeHtml(s.or)} ${signin}`;
    }
    html =
      `<span class="member-price__primary">${escapeHtml(reg)}</span>` +
      `<span class="member-price__teaser">${escapeHtml(member)} ${escapeHtml(s.memberPriceLabel)} | ${cta}</span>`;
  } else {
    // Card teaser: "$Y | $X Member price*"
    html =
      `<span class="member-price__primary">${escapeHtml(reg)}</span>` +
      `<span class="member-price__divider" aria-hidden="true"></span>` +
      `<span class="member-price__teaser">${escapeHtml(member)} ${escapeHtml(s.cardSuffix)}</span>`;
  }

  shell.innerHTML = html;
  shell.hidden = false;
  shell.dataset.mpRendered = 'true';
  shell.dataset.mpSource = source;

  const host = shell.closest('product-price');
  if (host) host.classList.add('member-pricing-active');

  announce(shell, member);
}

/**
 * Politely announces the resolved member price for screen readers (once).
 * @param {HTMLElement} shell
 * @param {string} memberFormatted
 */
function announce(shell, memberFormatted) {
  if (!CONFIG.strings.announcement) return;
  let region = shell.querySelector('.member-price__sr');
  if (!region) {
    region = document.createElement('span');
    region.className = 'member-price__sr visually-hidden';
    region.setAttribute('aria-live', 'polite');
    shell.appendChild(region);
  }
  region.textContent = CONFIG.strings.announcement.replace('{{ price }}', memberFormatted);
}

/**
 * Resolves + renders member pricing for one <product-price> element.
 * @param {HTMLElement} host
 * @param {Rule[]} rules
 */
function apply(host, rules) {
  const shell = host.querySelector('member-price');
  if (!shell) return;
  const item = readItem(host);
  if (!item) return;

  // The authed API is the source of truth for membership — NOT
  // settings.membership_tags (that drives member messaging elsewhere, not
  // pricing). A logged-in visitor with an applicable API discount gets the
  // member price; everyone else (logged-out, or no applicable rule) gets the
  // product's base-discount metafield teaser.
  const apiRule = CONFIG.loggedIn ? resolveDiscount(rules, item) : null;
  const isMember = apiRule != null && apiRule.discountAmount > 0;
  const discount = isMember ? apiRule.discountAmount : item.baseDiscount;
  if (discount == null || discount <= 0) {
    shell.hidden = true;
    shell.removeAttribute('data-mp-rendered');
    shell.removeAttribute('data-mp-source');
    host.classList.remove('member-pricing-active');
    return;
  }
  render(shell, item.price, discount, isMember ? 'api' : 'metafield', isMember, apiRule);
}

/**
 * Processes every unresolved <product-price> currently in the DOM.
 * @param {Rule[]} rules
 * @param {ParentNode} [root]
 */
function applyAll(rules, root = document) {
  root.querySelectorAll('product-price[data-mp-product-gid]').forEach((host) => apply(host, rules));
}

/**
 * Recomputes a single host after a variant change, pulling fresh data
 * attributes from the section-rendered HTML when present.
 * @param {Event} event
 */
function onVariantUpdate(event) {
  const detail = /** @type {any} */ (event).detail;
  const html = detail?.data?.html;
  loadProfile().then((rules) => {
    document.querySelectorAll('product-price[data-mp-product-gid]').forEach((host) => {
      if (html) {
        const fresh = html.querySelector(`product-price[data-block-id="${host.dataset.blockId}"]`);
        if (fresh) {
          host.dataset.mpVariantGid = fresh.dataset.mpVariantGid || host.dataset.mpVariantGid;
          host.dataset.mpPrice = fresh.dataset.mpPrice || host.dataset.mpPrice;
        }
      }
      apply(host, rules);
    });
  });
}

let observer = null;
let pendingRender = false;

/**
 * Watches for product-price elements added after load (faceted collections,
 * infinite scroll, predictive search, quick add) and resolves them.
 * @param {Rule[]} rules
 */
function observe(rules) {
  if (observer) return;
  observer = new MutationObserver((mutations) => {
    if (pendingRender) return;
    const hasNew = mutations.some((m) =>
      Array.from(m.addedNodes).some(
        (n) => n.nodeType === 1 && /** @type {Element} */ (n).querySelector?.('product-price[data-mp-product-gid]')
      )
    );
    if (!hasNew) return;
    pendingRender = true;
    requestAnimationFrame(() => {
      pendingRender = false;
      applyAll(rules);
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

function init() {
  if (!CONFIG) return;

  loadProfile().then((rules) => {
    applyAll(rules);
    observe(rules);
    document.addEventListener('variant:update', onVariantUpdate);
  });
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  // Joining or signing in can change membership — drop the cached profile so the
  // post-action page (after the join flow / login) resolves fresh pricing.
  // sessionStorage survives the navigation, so clearing here suffices.
  if (target.closest('.member-price__link')) clearProfileCache();

  // "Sign In" opens the login dialog by clicking the account button inside the
  // <shopify-account> shadow root (same target as product-form / cart-sync).
  if (target.closest('[data-member-price-signin]')) {
    event.preventDefault();
    const account = document.querySelector('shopify-account');
    const button = account && account.shadowRoot && account.shadowRoot.querySelector('button.account-button');
    if (button instanceof HTMLElement) button.click();
  }
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
